import { useCallback, useEffect, useState } from "react";
import config from "./config";

const base = `${config.API_URL.replace(/\/+$/, "")}/api/`;

async function read(path, signal, token) {
  const response = await fetch(base + path, {
    signal, cache: "no-store", headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = new Error("Dashboard data unavailable");
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// Refresh sections independently. Clear stale numbers after failures and ignore
// results from a previous account or unmounted page.
export function useDashboardData(kind, identity = "", enabled = true) {
  const token = localStorage.getItem("token") || "";
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let pending;
    setState({ data: null, loading: true, error: "" });
    async function refresh() {
      if (pending || document.hidden) return;
      const controller = new AbortController();
      pending = controller;
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        let data;
        if (kind === "signal") {
          data = identity ? await read(`User/GetSignal/${encodeURIComponent(identity)}`, controller.signal, token).catch(error => {
            if (error.status === 404) return null;
            throw error;
          }) : null;
          if (data !== null && (!data || typeof data !== "object" || Array.isArray(data))) throw new Error("Invalid signal");
        } else if (kind === "lastAnalysis" || kind === "lastTeamAnalysis") {
          data = await read(kind === "lastTeamAnalysis" ? "remote-analysis/team/last-completed" : "remote-analysis/last-completed", controller.signal, token);
          if (kind === "lastTeamAnalysis" && String(data?.teamId) !== identity) throw new Error("Team changed");
          if (!data || !Object.prototype.hasOwnProperty.call(data, "lastAnalysisAt")
              || (data.lastAnalysisAt !== null && (typeof data.lastAnalysisAt !== "string"
                || !/(Z|[+-]\d{2}:\d{2})$/i.test(data.lastAnalysisAt) || !Number.isFinite(Date.parse(data.lastAnalysisAt)))))
            throw new Error("Invalid analysis timestamp");
        } else if (kind === "team") {
          const summary = await read("team", controller.signal, token).catch(error => {
            if (error.status === 404) return null;
            throw error;
          });
          if (summary === null) data = { noTeam: true };
          else {
            if (!Array.isArray(summary.members) || !["Owner", "Admin", "Member"].includes(summary.role)
                || !Number.isInteger(summary.currentUserId)) throw new Error("Invalid team");
            const signals = await read(`User/GetTeam/${summary.currentUserId}`, controller.signal, token);
            if (!Array.isArray(signals)) throw new Error("Invalid team signals");
            const connected = new Set(signals.filter(isConnected).map(member => member.UserId));
            const owner = summary.ownerUserId != null ? summary.members.find(member => member.userId === summary.ownerUserId)
              : summary.members.find(member => member.role === "Owner");
            data = { ...summary, total: summary.members.length, ownerName: owner?.name?.trim() || owner?.email || "Unavailable",
              connected: summary.members.filter(member => connected.has(member.userId)).length };
          }
        } else if (kind === "meetings") {
          const result = await read("meetings", controller.signal, token);
          if (!Array.isArray(result?.meetings) || result.meetings.some(meeting => !meeting
              || !Number.isInteger(meeting.creatorUserId) || typeof meeting.meetingId !== "string" || !meeting.meetingId
              || typeof meeting.name !== "string"
              || (meeting.participantCount != null && (!Number.isInteger(meeting.participantCount) || meeting.participantCount < 0))))
            throw new Error("Invalid meetings");
          data = result.meetings.map(meeting => ({ ...meeting, isOwner: String(meeting.creatorUserId) === String(identity) }));
        } else if (kind === "services") {
          const result = await read("User/GetMyServicesAndSelections", controller.signal, token);
          if (!Array.isArray(result?.services)) throw new Error("Invalid services");
          const selected = result.services.filter(service => service.isSelected);
          if (selected.some(service => !Number.isInteger(service.serviceId)
              || typeof (service.serviceDisplayName || service.serviceName) !== "string")) throw new Error("Invalid services");
          const priority = status => status === "Outage" ? 0 : status === "Active incident" ? 1 : 2;
          data = selected.map(service => ({ id: service.serviceId, name: service.serviceDisplayName || service.serviceName,
            iconName: typeof service.serviceName === "string" ? service.serviceName : null,
            status: typeof service.serviceCurrentStatus === "string" && service.serviceCurrentStatus ? service.serviceCurrentStatus : "Status unavailable"
          })).sort((a, b) => priority(a.status) - priority(b.status));
        } else {
          data = await read("credits/account", controller.signal, token);
          if (!Number.isInteger(data?.availableReports) || data.availableReports < 0) throw new Error("Invalid account");
        }
        if (controller.signal.aborted) throw new Error("Request timed out");
        if (active && token === (localStorage.getItem("token") || "")) setState({ data, loading: false, error: "" });
      } catch (error) {
        if (active && token === (localStorage.getItem("token") || "")) setState({
          data: null, loading: false,
          error: error.status === 401 ? "Sign in again to refresh this section." : "This section could not be refreshed. Please try again.",
        });
      } finally {
        clearTimeout(timeout);
        if (pending === controller) pending = null;
      }
    }
    refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      active = false; pending?.abort(); clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [kind, identity, token, attempt, enabled]);
  return { ...state, retry };
}

export function isConnected(signal) {
  return Boolean(signal?.SignalOverallStatus && signal.SignalOverallStatus !== "Offline");
}
